import PageTabs, { TabItem } from "common/Tabs/PageTabs";
import React, { useCallback, useMemo, useState } from "react";
import { useQueryBotType } from "features/bots/common/hooks";
import {
  BotType,
  FiltersSlice,
  LanguageNameSpaces,
  WorkspaceType,
} from "@shared/types";
import Bots from "features/bots/Bots";
import BacktestingPage from "features/bots/BotForm/Backtesting/BacktestingPage/BacktestingPage";
import PublicBots from "features/bots/PublicBots/PublicBots";
import { DCA_PUBLIC_BOTS_PATH } from "common/constants";
import {
  BOT_TYPE_TO_BASE_BACKTESTING_PATH,
  BOT_TYPE_TO_BASE_MY_BOTS_PATH,
  BOT_TYPE_TO_BASE_SIGNALS_PATH,
} from "features/bots/common/constants";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import {
  BOT_TYPE_TO_BACKTESTING_FILTERS_SLICE,
  BOT_TYPE_TO_FILTERS_SLICE,
} from "@shared/features/bots/constants.ts";
import { publicBotsFilters } from "@shared/slices/filters.slice.ts";
import { MobileBotsTab } from "@shared/features/bots/types";
import styled, { useTheme } from "styled-components";
import SignalsPage from "features/signals/SignalsPage.tsx";
import {
  useSelectRealUser,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";

const BotsPage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType } = useQueryBotType();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  useDisablePageScroll(isBottomNavigator);
  const [tab, setTab] = useState<MobileBotsTab>();
  const user = useSelectRealUser();
  const { isSubUser } = useSubUserPermissions();
  const workspace = useSelector(selectWorkspace);
  const isTradingView =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  // TradingView-to-signals bots: asset managers and the admin workspace only
  // (never sub-users).
  const canUseTvToSignals =
    !isSubUser &&
    (!!user?.isAssetManager() || workspace === WorkspaceType.ADMIN);

  let filtersSlice: FiltersSlice;
  if (tab === MobileBotsTab.PUBLIC) filtersSlice = publicBotsFilters;
  else if (tab === MobileBotsTab.BACKTESTING) {
    filtersSlice = BOT_TYPE_TO_BACKTESTING_FILTERS_SLICE[botType];
  } else {
    filtersSlice = BOT_TYPE_TO_FILTERS_SLICE[botType];
  }

  usePageWrapper({
    filters: filtersSlice
      ? {
          actions: filtersSlice.actions,
          selectors: filtersSlice.selectors,
        }
      : undefined,
    showSearch: true,
    hideMobileFilters: true,
    dependencies: [tab, botType, filtersSlice],
  });

  const tabsItems: TabItem[] = useMemo(() => {
    const tabsItems = [
      {
        key: MobileBotsTab.MY_BOTS,
        label: isTradingView ? t("tabs.my_tv_bots") : t("tabs.my_bots"),
        children: (
          <Bots
            key="myBots"
            botType={isTradingView ? BotType.TRADING_VIEW : undefined}
          />
        ),
        link: BOT_TYPE_TO_BASE_MY_BOTS_PATH[
          isTradingView ? BotType.TRADING_VIEW : botType
        ],
      },
    ];

    if (botType === BotType.ASAP) {
      tabsItems.push({
        key: MobileBotsTab.PUBLIC,
        label: t("tabs.public"),
        children: <PublicBots />,
        link: DCA_PUBLIC_BOTS_PATH,
      });
    }
    if ([BotType.ASAP, BotType.SIGNAL].includes(botType)) {
      tabsItems.push({
        key: MobileBotsTab.BACKTESTING,
        label: t("tabs.backtesting"),
        children: <BacktestingPage />,
        link: BOT_TYPE_TO_BASE_BACKTESTING_PATH[botType],
      });
    }
    if (botType === BotType.SIGNAL) {
      tabsItems.push({
        key: MobileBotsTab.SIGNALS,
        label: t("bot_types.signals"),
        children: <SignalsPage />,
        link: BOT_TYPE_TO_BASE_SIGNALS_PATH[botType],
      });
    }
    if (isTradingView && canUseTvToSignals) {
      tabsItems.push({
        key: MobileBotsTab.TV_TO_SIGNALS,
        label: t("tabs.my_tv_to_signals_bots"),
        children: (
          <Bots key="tvToSignalsBots" botType={BotType.TRADING_VIEW_SIGNALS} />
        ),
        link: BOT_TYPE_TO_BASE_MY_BOTS_PATH[BotType.TRADING_VIEW_SIGNALS],
      });
    }
    return tabsItems;
  }, [botType, canUseTvToSignals, isTradingView, t]);

  const { isNewLook } = useTheme();
  const onTabChange = useCallback((tabItem: TabItem) => {
    setTab(tabItem.key as MobileBotsTab);
  }, []);

  // New look: every bot type gets the same chrome (header, tab bar, filter
  // row), so Grid and Hedging show their single "My Bots" tab too.
  if (
    !isNewLook &&
    (botType === BotType.GRID || botType === BotType.HEDGING)
  ) {
    return (
      <ContentWrapper>
        <Bots />
      </ContentWrapper>
    );
  }
  if (!isNewLook && isTradingView && !canUseTvToSignals) {
    return (
      <ContentWrapper>
        <Bots />
      </ContentWrapper>
    );
  }

  return (
    <PageTabs
      key={isTradingView ? BotType.TRADING_VIEW : botType}
      items={tabsItems}
      onTabChange={onTabChange}
      isInstant={isTradingView}
      urlChangeWait={isTradingView ? 0 : undefined}
    />
  );
};

export default React.memo(BotsPage);

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
